import { type JSX, useCallback, useRef, useState } from 'react';

interface ResizeHandleProps {
  maxWidth: number;
  minWidth: number;
  onResize: (width: number) => void;
}

const KEYBOARD_STEP = 20;

export function ResizeHandle({ maxWidth, minWidth, onResize }: ResizeHandleProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const onMouseMove = (moveEvent: MouseEvent): void => {
        const newWidth = Math.min(maxWidth, Math.max(minWidth, moveEvent.clientX));
        onResize(newWidth);
      };

      const onMouseUp = (): void => {
        setIsDragging(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [minWidth, maxWidth, onResize],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const el = handleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const currentWidth = rect.left;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onResize(Math.min(maxWidth, currentWidth + KEYBOARD_STEP));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onResize(Math.max(minWidth, currentWidth - KEYBOARD_STEP));
      }
    },
    [minWidth, maxWidth, onResize],
  );

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- focusable separator is an interactive splitter per WAI-ARIA */
  return (
    <div
      ref={handleRef}
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      className={`relative w-1 shrink-0 cursor-col-resize group transition-colors ${
        isDragging ? 'bg-primary/40' : 'hover:bg-primary/20'
      }`}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
    >
      {/* Visual pill / bulge indicator */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full transition-colors ${
          isDragging ? 'bg-primary' : 'bg-white/20 group-hover:bg-primary/60'
        }`}
      />
    </div>
  );
}
